import { HStack, VStack, Text, Box } from "@gluestack-ui/themed";
import { ISummaryCardPros } from "./types";
import { Icon } from "../../../components/core/Icon";
import { LinearSliceChart } from "../../../components/chart/LinearSliceChart";
import { useSummaryCardController } from "./controller";
import { formatAmountToString } from "./utils";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { IconNames } from "@repo/core/components/core/Icon/types";
import { useMemo } from "react";

export const SummaryCard = ({
  title,
  titleIcon,
  summaries,
  unit,
  iconVariant,
  date,
  variant,
  isScrollable,
  width: customWidth
}: ISummaryCardPros) => {
  const { width, webWidthContent, tabletWidthContent } = useDimensions();

  const cardWidth = useMemo(() => {
    if (width >= 768 && width <= 1024) {
      return tabletWidthContent / 2;
    }

    if (width > 1024) {
      return webWidthContent * 0.47;
    }

    if (!isScrollable) {
      return width - width * 0.12;
    }

    return width - width * 0.15;
  }, [width, webWidthContent, tabletWidthContent, isScrollable]);

  const { chartData, dateStr, totalAmountStr, theme } =
    useSummaryCardController({
      title,
      date,
      variant,
      titleIcon,
      summaries,
      unit,
      iconVariant
    });

  return (
    <VStack
      p="$4"
      h="$48"
      width={customWidth ?? cardWidth}
      bg={theme.backgroundColor}
      borderRadius="$2xl"
    >
      <VStack gap="$3">
        {/* HEADER */}
        <HStack alignItems="center" justifyContent="space-between">
          <HStack alignItems="center" gap="$2">
            <Icon
              name={titleIcon as IconNames}
              size={22}
              variant={iconVariant}
              color={theme.fontColor}
            />
            <Text
              fontSize="$sm"
              fontWeight={600}
              lineHeight="$sm"
              color={theme.fontColor}
            >
              {title}
            </Text>
          </HStack>

          <Box>
            <Text fontSize="$xs" fontWeight={400} color={theme.fontColor}>
              {dateStr}
            </Text>
          </Box>
        </HStack>

        {/* MAIN */}
        <Box>
          <Text fontSize={"$2xl"} fontWeight={600} color={theme.fontColor}>
            {totalAmountStr}
          </Text>
        </Box>

        {/* CHART */}
        <LinearSliceChart data={chartData} />

        {/* SUMMARIES LIST */}
        <VStack w="$full" gap="$1">
          {summaries?.map((summary) => {
            return (
              <HStack key={summary.label} gap="$1" alignItems="center">
                <Box
                  w="$3"
                  h={"$3"}
                  borderRadius={4}
                  backgroundColor={summary.chartColor}
                ></Box>
                <Text
                  fontSize={"$xs"}
                  fontWeight={600}
                  lineHeight={"$sm"}
                  color={theme.fontColor}
                >
                  {summary.label}: {formatAmountToString(summary.amount, unit)}
                </Text>
              </HStack>
            );
          })}
        </VStack>
      </VStack>
    </VStack>
  );
};
